/* eslint-disable @typescript-eslint/no-explicit-any */
import type { QuickMediumSignupPayload } from "../types";
import type { QuickMediumSocialSignupPayload } from "../types";

/**
 * Helper function to calculate age from birth date
 */
function normalizeBirthDate(birthDate: string): string {
  if (!birthDate) return "";

  const trimmed = birthDate.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
    return trimmed.slice(0, 10);
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return trimmed;
  }

  return parsed.toISOString().slice(0, 10);
}

function calculateAgeFromBirthDate(birthDate: string): number | null {
  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
}

/**
 * Sanitizer for quick medium signup payload.
 * Only includes essential fields required for quick signup.
 */
export function sanitizeQuickMediumSignupPayload(
  payload: Partial<QuickMediumSignupPayload>,
): QuickMediumSignupPayload {
  const sanitized: any = {};

  // Essential fields - REQUIRED
  const essentialFields = [
    "firstName",
    "lastName",
    "email",
    "birthDate",
    "phone",
    "password",
    "confirmPassword",
  ];

  // Copy essential fields
  for (const field of essentialFields) {
    sanitized[field] = payload[field as keyof QuickMediumSignupPayload] ?? "";
  }

  sanitized.birthDate = normalizeBirthDate(String(sanitized.birthDate ?? ""));

  // Handle guardian fields based on age
  const age = calculateAgeFromBirthDate(sanitized.birthDate);
  if (age === null || age < 18) {
    // Include guardian fields for minors
    sanitized.guardianName = payload.guardianName ?? "";
    sanitized.guardianPhone = payload.guardianPhone ?? "";
    sanitized.guardianRelationship = payload.guardianRelationship ?? "";
  }

  // Include optional fields only if they have values
  const optionalFields = [
    "documentType",
    "document",
    "contact",
    "rankId",
    "roleId",
    "initiationDate",
    "street",
    "number",
    "complement",
    "neighborhood",
    "city",
    "state",
    "zipCode",
    "code",
    "inviteCode",
    "signupCode",
    "isInviteFlow",
  ];

  for (const field of optionalFields) {
    const value = payload[field as keyof QuickMediumSignupPayload];
    if (value !== undefined && value !== null && value !== "") {
      sanitized[field] = value;
    }
  }

  return sanitized as QuickMediumSignupPayload;
}

/**
 * Sanitizer for quick medium social signup payload.
 * Uses quick essentials and omits password fields.
 */
export function sanitizeQuickMediumSocialSignupPayload(
  payload: Partial<QuickMediumSocialSignupPayload>,
): QuickMediumSocialSignupPayload {
  const sanitized: any = {};

  const essentialFields = [
    "firstName",
    "lastName",
    "email",
    "birthDate",
    "phone",
  ];

  for (const field of essentialFields) {
    sanitized[field] = payload[field as keyof QuickMediumSocialSignupPayload] ?? "";
  }

  sanitized.birthDate = normalizeBirthDate(String(sanitized.birthDate ?? ""));

  const age = calculateAgeFromBirthDate(sanitized.birthDate);
  if (age === null || age < 18) {
    sanitized.guardianName = payload.guardianName ?? "";
    sanitized.guardianPhone = payload.guardianPhone ?? "";
    sanitized.guardianRelationship = payload.guardianRelationship ?? "";
  }

  const optionalFields = [
    "documentType",
    "document",
    "contact",
    "rankId",
    "roleId",
    "initiationDate",
    "street",
    "number",
    "complement",
    "neighborhood",
    "city",
    "state",
    "zipCode",
    "code",
    "inviteCode",
    "signupCode",
    "isInviteFlow",
  ];

  for (const field of optionalFields) {
    const value = payload[field as keyof QuickMediumSocialSignupPayload];
    if (value !== undefined && value !== null && value !== "") {
      sanitized[field] = value;
    }
  }

  return sanitized as QuickMediumSocialSignupPayload;
}
